import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogHeader, DialogTitle, DialogContent, DialogFooter } from './dialog';
import { Button } from './button';
import { Label } from './label';
import { Checkbox } from './checkbox';
import { Search, RotateCcw, ChevronDown, ChevronRight, ArrowLeft } from 'lucide-react';
import { Input } from './input';
import { cn } from '../../lib/utils';

export interface ColumnDefinition {
  key: string;
  label: string;
  category?: string; // Optional category for grouping
  subColumns?: Array<{key: string, label: string}>; // Optional sub-columns (min, avg, max)
}

interface ColumnSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (selectedColumns: string[]) => void;
  columns: ColumnDefinition[];
  selectedColumns: string[];
  title?: string;
  defaultColumns?: string[]; // For reset functionality
}

export const ColumnSelectorModal: React.FC<ColumnSelectorModalProps> = ({
  isOpen,
  onClose,
  onSave,
  columns,
  selectedColumns,
  title = 'Select Columns',
  defaultColumns
}) => {
  const [localSelectedColumns, setLocalSelectedColumns] = useState<string[]>(selectedColumns);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedColumns, setExpandedColumns] = useState<Set<string>>(new Set());

  // Initialize local state when modal opens or selectedColumns change
  useEffect(() => {
    if (isOpen) {
      setLocalSelectedColumns(selectedColumns);
      setSearchQuery('');
      // Default: all columns collapsed (empty set)
      setExpandedColumns(new Set());
    }
  }, [isOpen, selectedColumns]);

  // Get all column keys including sub-columns for counting
  const getAllColumnKeys = useMemo(() => {
    const keys: string[] = [];
    columns.forEach(col => {
      keys.push(col.key);
      if (col.subColumns) {
        col.subColumns.forEach(sub => keys.push(sub.key));
      }
    });
    return keys;
  }, [columns]);

  // Filter columns based on search query (includes sub-columns)
  const filteredColumns = useMemo(() => {
    if (!searchQuery.trim()) {
      return columns;
    }
    const query = searchQuery.toLowerCase();
    return columns.filter(col => {
      // Check parent column
      const parentMatches = 
        col.label.toLowerCase().includes(query) ||
        col.key.toLowerCase().includes(query) ||
        (col.category && col.category.toLowerCase().includes(query));
      
      // Check sub-columns
      const subMatches = col.subColumns?.some(sub => 
        sub.label.toLowerCase().includes(query) ||
        sub.key.toLowerCase().includes(query)
      );
      
      return parentMatches || subMatches;
    });
  }, [columns, searchQuery]);

  // Group columns by category
  const groupedColumns = useMemo(() => {
    const groups: Record<string, ColumnDefinition[]> = {};
    filteredColumns.forEach(col => {
      const category = col.category || 'Other';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(col);
    });
    return groups;
  }, [filteredColumns]);

  // Get all visible column keys (parent + sub-columns)
  const getAllVisibleKeys = useMemo(() => {
    const keys: string[] = [];
    filteredColumns.forEach(col => {
      keys.push(col.key);
      if (col.subColumns) {
        col.subColumns.forEach(sub => keys.push(sub.key));
      }
    });
    return keys;
  }, [filteredColumns]);

  // Check if all visible columns (including sub-columns) are selected
  const allSelected = useMemo(() => {
    if (getAllVisibleKeys.length === 0) return false;
    return getAllVisibleKeys.every(key => localSelectedColumns.includes(key));
  }, [getAllVisibleKeys, localSelectedColumns]);

  // Check if some (but not all) visible columns are selected
  const someSelected = useMemo(() => {
    const selectedCount = getAllVisibleKeys.filter(key => localSelectedColumns.includes(key)).length;
    return selectedCount > 0 && selectedCount < getAllVisibleKeys.length;
  }, [getAllVisibleKeys, localSelectedColumns]);

  const handleColumnToggle = (columnKey: string) => {
    setLocalSelectedColumns(prev => {
      // Check if this is a parent column
      const column = columns.find(col => col.key === columnKey);
      
      if (prev.includes(columnKey)) {
        // Unselecting parent column - also unselect all its sub-columns
        const keysToRemove = [columnKey];
        if (column?.subColumns) {
          column.subColumns.forEach(sub => keysToRemove.push(sub.key));
        }
        return prev.filter(key => !keysToRemove.includes(key));
      } else {
        // Selecting parent column - just add the parent (sub-columns remain independent)
        return [...prev, columnKey];
      }
    });
  };

  const handleSelectAll = () => {
    if (allSelected) {
      // Deselect all visible columns (parent + sub-columns)
      setLocalSelectedColumns(prev => 
        prev.filter(key => !getAllVisibleKeys.includes(key))
      );
    } else {
      // Select all visible columns (parent + sub-columns)
      const newSelected = Array.from(new Set([...localSelectedColumns, ...getAllVisibleKeys]));
      setLocalSelectedColumns(newSelected);
    }
  };

  const toggleExpanded = (columnKey: string) => {
    setExpandedColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        newSet.delete(columnKey);
      } else {
        newSet.add(columnKey);
      }
      return newSet;
    });
  };

  const handleReset = () => {
    if (defaultColumns) {
      setLocalSelectedColumns(defaultColumns);
    } else {
      // Default: select all columns (parent + sub-columns)
      setLocalSelectedColumns(getAllColumnKeys);
    }
  };

  const handleSave = () => {
    // Ensure at least 2 columns are selected
    if (localSelectedColumns.length < 2) {
      // If less than 2 selected, don't save (button should be disabled anyway)
      return;
    }
    onSave(localSelectedColumns);
    onClose();
  };

  const handleCancel = () => {
    // Reset to original selectedColumns
    setLocalSelectedColumns(selectedColumns);
    onClose();
  };

  const selectedCount = localSelectedColumns.length;
  const totalCount = getAllColumnKeys.length;

  // Check if at least 2 columns are selected
  const canSave = selectedCount >= 2;

  return (
    <>
      <style>{`
        .column-scrollable::-webkit-scrollbar {
          width: 8px;
        }
        .column-scrollable::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        .column-scrollable::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        .column-scrollable::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
      <Dialog open={isOpen} onOpenChange={handleCancel}>
        <DialogContent className="max-w-6xl w-[95vw] h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center gap-4">
            <Button
              onClick={handleCancel}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              variant="outline"
            >
              <ArrowLeft className="h-5 w-5 text-gray-500" />
            </Button>
            <DialogTitle>{title}</DialogTitle>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-3 pt-4 pl-1">
          {/* Search and Controls */}
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search columns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleSelectAll}
                className="whitespace-nowrap"
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </Button>
              {defaultColumns && (
                <Button
                  onClick={handleReset}
                  className="whitespace-nowrap"
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              )}
            </div>
          </div>

          {/* Selected Count */}
          <div className="text-sm text-gray-600 flex-shrink-0 bg-blue-50 px-4 py-2 rounded-lg border">
            <span className="font-medium">{selectedCount} of {totalCount} columns selected</span>
            {selectedCount < 2 && (
              <span className="text-red-600 ml-2 font-medium">(Minimum 2 columns required)</span>
            )}
          </div>

          {/* Columns List - Scrollable layout for multiple categories */}
          <div className="flex-1 border rounded-lg bg-gray-50 p-4 overflow-hidden">
            {Object.keys(groupedColumns).length === 0 ? (
              <div className="text-center text-gray-500 py-12">
                <p className="text-lg">No columns found matching "{searchQuery}"</p>
                <p className="text-sm mt-2">Try a different search term</p>
              </div>
            ) : (
              <div className="h-full overflow-y-auto overflow-x-hidden column-scrollable">
                <div className={cn(
                  "grid gap-4",
                  Object.keys(groupedColumns).length <= 2 ? "grid-cols-2" : "grid-cols-1"
                )}>
                  {Object.entries(groupedColumns)
                    .sort(([a], [b]) => {
                      // Ensure Basic Info comes first, then Parameters, then others alphabetically
                      if (a === 'Basic Info') return -1;
                      if (b === 'Basic Info') return 1;
                      if (a === 'Parameters') return -1;
                      if (b === 'Parameters') return 1;
                      return a.localeCompare(b);
                    })
                    .map(([category, categoryColumns]) => {
                    const isBasicInfo = category === 'Basic Info';
                    const isParameters = category === 'Parameters';
                    
                    return (
                      <div key={category} className={cn(
                        "bg-white rounded-lg p-4 shadow-sm border border-gray-200 flex flex-col overflow-hidden",
                        (isBasicInfo || isParameters) ? "min-h-[300px] max-h-[600px]" : "min-h-[200px] max-h-[400px]"
                      )}>
                        <h3 className="text-base font-bold text-gray-800 mb-3 pb-2 border-b-2 border-[#EB1C37] uppercase tracking-wide flex-shrink-0">
                          {category}
                        </h3>
                        <div 
                          className={cn(
                            "flex-1 overflow-y-auto overflow-x-hidden pr-2 column-scrollable",
                            isBasicInfo ? "grid grid-cols-2 gap-2 content-start" : isParameters ? "grid grid-cols-2 gap-2 content-start" : "space-y-2"
                          )}
                          style={{
                            scrollbarWidth: 'thin',
                            scrollbarColor: '#cbd5e1 #f1f5f9'
                          }}
                        >
                        {categoryColumns.map((column) => {
                          const isSelected = localSelectedColumns.includes(column.key);
                          const hasSubColumns = column.subColumns && column.subColumns.length > 0;
                          const isExpanded = expandedColumns.has(column.key);
                          const allSubColumnsSelected = hasSubColumns && column.subColumns?.every(sub => localSelectedColumns.includes(sub.key));
                          const someSubColumnsSelected = hasSubColumns && column.subColumns?.some(sub => localSelectedColumns.includes(sub.key)) && !allSubColumnsSelected;
                          
                          return (
                            <div key={column.key} className={cn(
                              "space-y-1",
                              isBasicInfo && hasSubColumns ? "col-span-2" : ""
                            )}>
                              {/* Parent Column */}
                              <div
                                className={cn(
                                  "flex items-center gap-2 p-3 rounded-md border transition-all",
                                  isSelected
                                    ? "bg-blue-50 shadow-sm"
                                    : "bg-white border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                                )}
                              >
                                {hasSubColumns && (
                                  <button
                                    type="button"
                                    className="expand-icon flex-shrink-0 p-1 hover:bg-gray-200 rounded transition-colors"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleExpanded(column.key);
                                    }}
                                    aria-label={isExpanded ? "Collapse" : "Expand"}
                                  >
                                    {isExpanded ? (
                                      <ChevronDown className="h-4 w-4 text-gray-600" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4 text-gray-600" />
                                    )}
                                  </button>
                                )}
                                {!hasSubColumns && <div className="w-6" />}
                                <Checkbox
                                  id={column.key}
                                  checked={isSelected}
                                  onCheckedChange={() => handleColumnToggle(column.key)}
                                  className="flex-shrink-0"
                                />
                                <Label
                                  htmlFor={column.key}
                                  className="flex-1 cursor-pointer text-sm font-medium text-gray-700 leading-tight select-none"
                                  onClick={() => handleColumnToggle(column.key)}
                                >
                                  {column.label}
                                </Label>
                              </div>
                              
                              {/* Sub-columns */}
                              {hasSubColumns && isExpanded && column.subColumns && (
                                <div className="ml-8 space-y-1.5">
                                  {column.subColumns.map((subColumn) => {
                                    const isSubSelected = localSelectedColumns.includes(subColumn.key);
                                    return (
                                      <div
                                        key={subColumn.key}
                                        className={cn(
                                          "flex items-center gap-2 p-2.5 rounded-md border transition-all",
                                          isSubSelected
                                            ? "bg-blue-50 shadow-sm"
                                            : "bg-gray-50 border-gray-200 hover:border-gray-300 hover:bg-gray-100"
                                        )}
                                      >
                                        <div className="w-4" />
                                        <Checkbox
                                          id={subColumn.key}
                                          checked={isSubSelected}
                                          onCheckedChange={() => handleColumnToggle(subColumn.key)}
                                          className="flex-shrink-0"
                                          disabled={!isSelected}
                                        />
                                        <Label
                                          htmlFor={subColumn.key}
                                          className={cn(
                                            "flex-1 cursor-pointer text-xs font-medium leading-tight select-none",
                                            isSelected ? "text-gray-700" : "text-gray-400 cursor-not-allowed"
                                          )}
                                          onClick={() => {
                                            if (isSelected) {
                                              handleColumnToggle(subColumn.key);
                                            }
                                          }}
                                        >
                                          {subColumn.label}
                                        </Label>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

